import { Route } from '@angular/router';
import { getNavigationGroup } from '../../core/config/app-navigation';
import { pageAccessGuard } from '../../core/guards/page-access.guard';
import { nuevoRegaloFlowExitGuard } from './guards/nuevo-regalo-flow-exit.guard';
import { nuevaAgendaFlowExitGuard } from './guards/nueva-agenda-flow-exit.guard';
import { ordenesPagarExitGuard } from './guards/ordenes-pagar-exit.guard';

const ordenesNavigation = getNavigationGroup('ordenes');
const nuevoRegaloNavigationItem = ordenesNavigation.items.find(
  (item) => item.key === 'nuevo-regalo',
);
const nuevaAgendaNavigationItem = ordenesNavigation.items.find(
  (item) => item.key === 'nueva-agenda',
);

const ordenesRouteLoaders = {
  ordenes: () => import('./pages/ordenes-list/ordenes-list.page').then((m) => m.OrdenesListPage),
  clientes: () => import('./pages/clientes/clientes.page').then((m) => m.ClientesPage),
};

const mappedOrdenesRoutes: Route[] = ordenesNavigation.items
  .filter((item) => item.key !== 'nuevo-regalo' && item.key !== 'nueva-agenda')
  .map((item) => {
    const loadComponent = ordenesRouteLoaders[item.key as keyof typeof ordenesRouteLoaders];

    if (!loadComponent) {
      throw new Error(`No loadComponent configured for ordenes route "${item.key}".`);
    }

    return {
      path: item.path.replace(/^\//, ''),
      data: {
        pageKey: item.pageKey,
        title: item.title,
        description: item.description,
      },
      canActivate: [pageAccessGuard],
      loadComponent,
    };
  });

if (!nuevoRegaloNavigationItem) {
  throw new Error('No navigation item configured for "nuevo-regalo".');
}

if (!nuevaAgendaNavigationItem) {
  throw new Error('No navigation item configured for "nueva-agenda".');
}

export const ordenesRoutes: Route[] = [
  {
    path: nuevoRegaloNavigationItem.path.replace(/^\//, ''),
    data: {
      pageKey: nuevoRegaloNavigationItem.pageKey,
      title: nuevoRegaloNavigationItem.title,
      description: nuevoRegaloNavigationItem.description,
    },
    canActivate: [pageAccessGuard],
    loadComponent: () =>
      import('./pages/nuevo-regalo/flow/nuevo-regalo-flow.page').then((m) => m.NuevoRegaloFlowPage),
    canDeactivate: [nuevoRegaloFlowExitGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/nuevo-regalo/nuevo-regalo.page').then((m) => m.NuevoRegaloPage),
      },
      {
        path: 'personalizar',
        loadComponent: () =>
          import('./pages/nuevo-regalo/personalizar-regalo/personalizar-regalo.page').then(
            (m) => m.PersonalizarRegaloPage,
          ),
      },
      {
        path: 'metodo-pago',
        loadComponent: () =>
          import('./pages/nuevo-regalo/metodo-pago/metodo-pago.page').then(
            (m) => m.RegaloMetodoPagoPage,
          ),
      },
      {
        path: 'pago/pendiente',
        loadComponent: () =>
          import('./pages/nuevo-regalo/pago-pendiente/pago-pendiente.page').then(
            (m) => m.PagoPendientePage,
          ),
      },
      {
        path: 'pago/procesando',
        loadComponent: () =>
          import('./pages/nuevo-regalo/pago-procesando/pago-procesando.page').then(
            (m) => m.RegaloPagoProcesandoPage,
          ),
      },
      {
        path: 'pago/multipago',
        loadComponent: () =>
          import('./pages/nuevo-regalo/pago-multipago/pago-multipago.page').then(
            (m) => m.RegaloPagoMultipagoPage,
          ),
      },
      {
        path: 'pago/confirmado',
        loadComponent: () =>
          import('./pages/nuevo-regalo/pago-confirmado/pago-confirmado.page').then(
            (m) => m.PagoConfirmadoPage,
          ),
      },
      {
        path: 'imprimir',
        loadComponent: () =>
          import('./pages/nuevo-regalo/imprimir-regalos/imprimir-regalos.page').then(
            (m) => m.ImprimirRegalosPage,
          ),
      },
    ],
  },
  {
    path: nuevaAgendaNavigationItem.path.replace(/^\//, ''),
    data: {
      pageKey: nuevaAgendaNavigationItem.pageKey,
      title: nuevaAgendaNavigationItem.title,
      description: nuevaAgendaNavigationItem.description,
    },
    canActivate: [pageAccessGuard],
    loadComponent: () =>
      import('./pages/nueva-agenda/flow/nueva-agenda-flow.page').then((m) => m.NuevaAgendaFlowPage),
    canDeactivate: [nuevaAgendaFlowExitGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/nueva-agenda/nueva-agenda.page').then((m) => m.NuevaAgendaPage),
      },
      {
        path: 'facturacion',
        loadComponent: () =>
          import('./pages/nueva-agenda/facturacion/facturacion.page').then(
            (m) => m.FacturacionPage,
          ),
      },
      {
        path: 'metodo-pago',
        loadComponent: () =>
          import('./pages/nueva-agenda/metodo-pago/metodo-pago.page').then((m) => m.MetodoPagoPage),
      },
      {
        path: 'pago/pendiente',
        loadComponent: () =>
          import('./pages/nueva-agenda/pago-pendiente/pago-pendiente.page').then(
            (m) => m.PagoPendientePage,
          ),
      },
      {
        path: 'pago/procesando',
        loadComponent: () =>
          import('./pages/nueva-agenda/pago-procesando/pago-procesando.page').then(
            (m) => m.PagoProcesandoPage,
          ),
      },
      {
        path: 'pago/multipago',
        loadComponent: () =>
          import('./pages/nueva-agenda/pago-multipago/pago-multipago.page').then(
            (m) => m.PagoMultipagoPage,
          ),
      },
      {
        path: 'confirmada',
        loadComponent: () =>
          import('./pages/nueva-agenda/agenda-confirmada/agenda-confirmada.page').then(
            (m) => m.AgendaConfirmadaPage,
          ),
      },
    ],
  },
  ...mappedOrdenesRoutes,
  {
    path: 'ordenes/ordenes/:uuid/pagar',
    data: {
      pageKey: 'ordenes.ordenes',
      title: 'Cobrar orden',
      description: 'Procesar el cobro con tarjeta de una orden existente.',
    },
    canActivate: [pageAccessGuard],
    canDeactivate: [ordenesPagarExitGuard],
    loadComponent: () =>
      import('./pages/ordenes-pagar/ordenes-pagar.page').then((m) => m.OrdenesPagarPage),
  },
  {
    path: 'ordenes/ordenes/:uuid/pagar-multipago',
    data: {
      pageKey: 'ordenes.ordenes',
      title: 'Cobrar orden (multipago)',
      description: 'Cobro secuencial por método de pago de una orden existente.',
    },
    canActivate: [pageAccessGuard],
    canDeactivate: [ordenesPagarExitGuard],
    loadComponent: () =>
      import('./pages/ordenes-pagar-multipago/ordenes-pagar-multipago.page').then(
        (m) => m.OrdenesPagarMultipagoPage,
      ),
  },
];
